import {DOM, IDocumentFragment} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlObj, IJmlSet, JML} from "/@lib@/commons/xml/jml.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {XmlDeleteMsg, XmlInsertMsg, XmlStrMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {CARD, SchemaDom, SkContext, SkNode} from "/@lib@/edit/schema/schema.js";
import {EAnnotLevel, SkAnnotEltUnknown, SkAnnotMissing, SkAnnotWrongValue} from "/@lib@/edit/schema/schemaAnnots.js";
import {ISchemaBuilder, SK_NS} from "/@lib@/edit/schema/schemaBuilder.js";
import {
	collapseSpOnMergeTextNodes,
	EGramLevelType,
	ISkImporter,
	ISkMetaNode,
	OSkImportCtxInternal,
	OSkImportDatas,
	OSkMCopyScope,
	OSkPasteContext,
	SkImpGoUp,
	SkImpSimple,
	SkImpSplitUp,
	SkMBase,
	SKMETA,
	SKMETALIB,
	SkMMeta,
	SkMObject
} from "/@lib@/edit/schema/schemaMeta.js";
import {ISkRuleObj, SkRule, SkRuleChoice, SkRuleComment, SkRuleElt, SkRuleEltUnknown, SkRuleNode} from "/@lib@/edit/schema/schemaPatterns.js";
import {MsgOt} from "/@lib@/edit/ot/houseOt.js";

export interface ISkMInText {
	isParaParent?: boolean
}

/** Racine de texte riche.*/
class SkMText extends SkMBase implements ISkMInText {

	initSkRule(skRule: SkRuleNode & ITextProps, confRule: Element): void {
		let att = confRule.getAttributeNS(SK_NS, 'maxLength');
		if (att) skRule.maxLength = parseInt(att);
		att = confRule.getAttributeNS(SK_NS, 'maxLengthWarn');
		if (att) skRule.maxLengthWarn = parseInt(att);
		skRule.commentRule = skruleCmtBlackHole;
	}

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	get isParaParent() {return true}

	// TODO annot pour maxLength et maxLengthWarn
	onExecRules(skCtx: SkContext, rule: ISkRuleObj & ITextProps, nodeOrAttr: Element): void {
		if (!skCtx.execOptions.genAnnots) return;
		//console.log("SkMText.checkGlobalRules:::", nodeOrAttr);
		if (rule.maxLength > 0 || rule.maxLengthWarn > 0) {
			const len = this.computeLen(nodeOrAttr);
			if (len > rule.maxLength && rule.maxLength > 0) {
				skCtx.addAnnot(new SkAnnotWrongValue().init(nodeOrAttr, `Nombre de caractères supérieur à ${rule.maxLength}`));
			} else if (len > rule.maxLengthWarn && rule.maxLengthWarn > 0) {
				skCtx.addAnnot(new SkAnnotWrongValue().init(nodeOrAttr, `Nombre de caractères recommandé inférieur à ${rule.maxLengthWarn}`, EAnnotLevel.warning));
			}
		}
	}

	computeLen(node: Element): number {
		let len = 0;
		const it = node.ownerDocument.createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
			acceptNode(n: Text | Element): number {
				if (n instanceof Text) return NodeFilter.FILTER_ACCEPT;
				return n.namespaceURI === DOM.SCCORE_NS ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_REJECT;
			}
		})
		let n: Node;
		while ((n = it.nextNode()) != null) len += n.nodeValue.length;
		return len;
	}

	needTextGlobalCheck(rule: ISkRuleObj & ITextProps) {return rule.maxLength > 0 || rule.maxLengthWarn > 0}
}

type ITextProps = {
	maxLength?: number
	maxLengthWarn?: number
}

SKMETALIB.registerMetaNode(new SkMText('Text'));

/** Noeud du schéma Text dont l'attribut role est inconnu. Gère la mutation vers un role connu. */
class SkMTxtRoleUnknown extends SkMBase {

	/** XXX peut-etre inline aussi, à voir si utile de distinguer... */
	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	initSkRule(skRule: SkRuleNode & ITxtRoleUnknownProps, confRule: Element): void {
		skRule.initContentRule(SkRuleChoice.ANY); //on force le contentRule à ANY et non EMPTY.
		skRule.mutateRoleTo = confRule.getAttributeNS(SK_NS, 'mutateRoleTo');
		skRule.knownRoles = confRule.getAttributeNS(SK_NS, 'knownRoles').split('|');
		skRule.knownRoles.splice(skRule.knownRoles.length - 1, 1);
		skRule.commentRule = skruleCmtBlackHole;
	}

	ruleMatch(skRule: SkRule & ITxtRoleUnknownProps, node: Node | IJmlObj): boolean {
		return skRule.knownRoles.indexOf(((node instanceof Element ? node.getAttribute(SkMTxtRolable.ATTNAME_role) : (node as IJmlObj)[SkMTxtRolable.ATTNAME_role]) || "")) < 0;
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.autoMutate) {
			const rule = skCtx.skNode.rule as ITxtRoleUnknownProps;
			//TODO chercher un role dont les metas du contenu seraient compatibles (voir tout le contenu si
			// on impl les restrictions de balises dans le texte) avant de prendre le role par défaut.
			(skCtx.execOptions.mutations || (skCtx.execOptions.mutations = [])).push(
				new XmlStrMsg().init(XA.append(XA.from(nodeOrAttr), SkMTxtRolable.ATTNAME_role), rule.mutateRoleTo)
			);
		} else if (skCtx.execOptions.genAnnots) {
			skCtx.addAnnot(new SkAnnotEltUnknown().init(nodeOrAttr as Element, skCtx.skNode.rule));
		}
	}
}

SKMETALIB.registerMetaNode(new SkMTxtRoleUnknown('TxtRoleUnknown'));

type ITxtRoleUnknownProps = {
	mutateRoleTo?: string
	knownRoles?: string[]
}

abstract class SkMTxtRolable extends SkMObject {
	static ATTNAME_role = "role";

	initSkRule(skRule: SkRule & ITxtRolableProps, confRule: Element, schemaBuilder: ISchemaBuilder): void {
		skRule.role = confRule.getAttributeNS(SK_NS, 'role');
		skRule.commentRule = skruleCmtBlackHole;
	}

	ruleMatch(skRule: SkRule & ITxtRolableProps, node: Node | IJmlNode): boolean {
		return ((node instanceof Element ? node.getAttribute(SkMTxtRolable.ATTNAME_role) : (node as IJmlObj)[SkMTxtRolable.ATTNAME_role]) || "") === (skRule.role || "");
	}

	onCreateNode(rule: SkRuleNode & ITxtRolableProps, dst: Node): void {
		if (rule.role) (dst as Element).setAttribute(SkMTxtRolable.ATTNAME_role, rule.role);
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.autoCleanup && skCtx.execOptions.corrections.length > 0) skipUnknownTxtNode(skCtx, rule as SkRuleNode, nodeOrAttr as Element);
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
	}
}

function textGlobalCheck(skMeta: ISkMetaNode, skCtx: SkContext, rule: ISkRuleObj, node: Node) {
	if (skCtx.execOptions.resetAll || !skCtx.execOptions.genAnnots) return;
	//On remonte pour trouver le SkMText
	let pNode = node.parentElement;
	const schemaDom = skCtx.skNode.schemaDom;
	while (pNode) {
		const pRule = schemaDom.getSkNode(pNode)?.rule;
		if (!pRule) return; //cas où pNode a été reset via SchemDom.resetNode() et son parent est en shouldRevalid, donc sera retraité plus tard
		if (pRule.skMeta instanceof SkMText) {
			if (pRule.skMeta.needTextGlobalCheck(pRule)) (skCtx.execOptions.shouldRevalid || (skCtx.execOptions.shouldRevalid = new Set())).add(pNode);
			break;
		}
		pNode = pNode.parentElement;
	}
}

type ITxtRolableProps = {
	role?: string
}

export class SkMPara extends SkMObject {

	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {return true}

	/**
	 * Enveloppe un ISkImporter insérant un élément de type paraSibling qui gère la remontée au dessus
	 * du paragraphe ou le split du paragraphe.
	 * NOTE : algo utilisable que si le paraParent n'est pas virtuel et en contexte OSkPasteContext (pas OSkPasteSearchContext)
	 */
	static wrapImportForParaSiblings(imports: ISkImporter[], paraParent: Node, ctx: OSkImportCtxInternal) {
		const startDepth = SKMETA.ctxRealStartDepth(ctx) as number;
		const startNode = XA.findDom(ctx.sel.start, ctx.schemaDom.nodeRoot, startDepth);
		//on évalue si le point d'insertion est en début de para
		let startAtBorderPara = true;
		let node = startNode;
		let depth = startDepth;
		while (node && node !== paraParent) {
			if (!SKMETA.isAtBorder(node, true, ctx.sel.start[depth--] as number, ctx.schemaDom)) {
				startAtBorderPara = false;
				break;
			}
			node = node.parentNode;
		}
		//on évalue si le point d'insertion est en fin de para
		let endAtBorderPara = true;
		let endXa: IXAddr;
		if (XA.isCollapsed(ctx.sel)) {
			depth = startDepth;
			node = startNode;
			endXa = ctx.sel.start;
		} else {
			depth = ctx.sel.end.length - 1;
			node = XA.findDom(ctx.sel.end, ctx.schemaDom.nodeRoot, depth);
			endXa = ctx.sel.end;
		}
		let d = 0;
		while (node && node !== paraParent) {
			if (!SKMETA.isAtBorder(node, false, endXa[depth--] as number + d, ctx.schemaDom)) {
				endAtBorderPara = false;
				break;
			}
			d = 1; //on check la fin, après le 1er container on décale pour voir si on pointe le dernier noeud.
			node = node.parentNode;
		}

		const splitDepth = ctx.sel.start.length - 1 - DOM.computeDepth(paraParent);
		if (startAtBorderPara && endAtBorderPara) {
			for (let i = 0; i < imports.length; i++) {
				imports[i] = new SkImpGoUp(ctx.schemaDom, imports[i], splitDepth, 'replace');
			}
		} else if (startAtBorderPara) {
			for (let i = 0; i < imports.length; i++) {
				imports[i] = new SkImpGoUp(ctx.schemaDom, imports[i], splitDepth, 'before');
			}
		} else if (endAtBorderPara) {
			for (let i = 0; i < imports.length; i++) {
				imports[i] = new SkImpGoUp(ctx.schemaDom, imports[i], splitDepth, 'after');
			}
		} else {
			for (let i = 0; i < imports.length; i++) {
				imports[i] = new SkImpSplitUp(ctx.schemaDom, imports[i], splitDepth);
			}
		}
	}

	initSkRule(skRule: SkRule, confRule: Element, schemaBuilder: ISchemaBuilder): void {
		super.initSkRule(skRule, confRule, schemaBuilder);
		(skRule as SkRuleElt).noAutoComplete = true;
		skRule.commentRule = skruleCmtBlackHole;
	}


	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	exportAsText(node: Node, rule: SkRule, buf: string[]) {
		super.exportAsText(node, rule, buf);
		buf.push("\n");
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("p"));
		super.exportAsHtml(node, rule, p);
	}

	exportContainerNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (parentTarget instanceof DocumentFragment) (parentTarget as IDocumentFragment).xmlSpacePreserve = true;
		return super.exportContainerNode(src, parentTarget, scope, fromDepth, toDepth);
	}

	async tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		//On recherche des contenus inline
		const paraRule = SKMETA.ctxLastSkRule(ctx);
		let first = true;
		const parentRule = SKMETA.ctxFindSkRuleInAnc(ctx, () => (first = !first));
		const isSinglePara = parentRule instanceof SkRuleElt ? !CARD.isRepeatable(parentRule.contentRule.getRealCardSubRule(paraRule)) : true;
		if (!isSinglePara) ctx.abortIfNotInline = true;
		const known = await this.findKnownNodes(ctx, content, isSinglePara, cache);
		if (isSinglePara) return known ? [new SkImpSimple(ctx, known.nodes, known.malus)] : null;
		if (known && known.malus === 0) return [new SkImpSimple(ctx, known.nodes, known.malus)];
		//Inline pas parfait
		//on remonte au niveau des paraParent
		ctx.abortIfNotInline = false;
		const newCtx = SKMETA.newImportCtxUp(ctx, (skRule: SkRule) => (skRule.skMeta as ISkMInText).isParaParent);
		if (!newCtx) return null;
		const imports = await SKMETA.ctxLastSkRule(newCtx).skMeta.tryPasteNodes(newCtx, content, cache);
		if (known && (!imports || imports[0]?.malus > known.malus)) {
			//On garde la version inline jugée meilleure
			return [new SkImpSimple(ctx, known.nodes, known.malus)];
		}
		if (imports) {
			if (ctx.skRuleStack) {
				return imports.map((imp) => new SkImpGoUp(ctx.schemaDom, imp, ctx.sel.start.length - newCtx.sel.start.length, 'replace', imp.malus));
			} else {
				let parent = newCtx.ctn;
				if (newCtx.skNode?.rule.skMeta instanceof SkMLI && imports.length === 1 && imports[0] instanceof SkImpSimple) {
					const toImport = (imports[0] as SkImpSimple).contentToImport
					if (Array.isArray(toImport) && toImport.length === 1 && toImport[0].nodeName === newCtx.ctn.parentElement?.nodeName) {
						//Même liste, on fusionne les entrées dans la même liste.
						const list: Node[] = [];
						for (let ch = (toImport[0] as Element).firstElementChild; ch; ch = ch.nextElementSibling) {list.push(ch)}
						(imports[0] as SkImpSimple).contentToImport = list;
						parent = newCtx.ctn.parentNode;
					}
				}
				SkMPara.wrapImportForParaSiblings(imports, parent, ctx);
			}
		}
		return imports;
	}

	normChars(schemaDom: SchemaDom, ctn: Text | Attr, msgs: MsgOt[], noTrim?: boolean) {
		normalizeChars(schemaDom, ctn, ctn instanceof Text ? ctn.parentElement : ctn, msgs, noTrim)
	}

	onMergeTextNodes(schemaDom: SchemaDom, ctn: Text, nextTxts: string[]): string {
		return collapseSpOnMergeTextNodes(ctn.nodeValue, nextTxts);
	}

	onCreateNode(rule: SkRuleNode, dst: Node) {
		(dst as Element).setAttributeNS(DOM.XML_NS, "xml:space", "preserve");
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node): void {
		if (skCtx.execOptions.autoNormChars) {
			if (!nodeOrAttr.hasChildNodes() && nodeOrAttr.parentNode) {
				//Elimination des para vides optionnels.
				const skNodeParent = skCtx.skNode.schemaDom.getSkNode(nodeOrAttr.parentNode);
				if (skNodeParent && skNodeParent.rule.contentRule.isEltRemovable(nodeOrAttr as Element)) {
					skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(nodeOrAttr), 1));
					return; //para supprimé -> pas d'autoCleanup.
				}
			}
		}
		if (skCtx.execOptions.autoCleanup && skCtx.execOptions.corrections.length > 0) skipUnknownTxtNode(skCtx, rule as SkRuleNode, nodeOrAttr as Element);
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
	}
}

SKMETALIB.registerMetaNode(new SkMPara('Para'));

function normalizeChars(schemaDom: SchemaDom, ctn: Text | Attr, para: Attr | Element | null, msgs: MsgOt[], noTrim?: boolean) {
	const str = ctn.nodeValue;
	let trimStart = false;
	let trimEnd = false;
	if (!noTrim && para) {
		//trimEnd
		const endSpLen = DOM.txtEndSpLen(str);
		if (endSpLen > 0 && shouldTrimEnd(schemaDom, ctn, para)) {
			trimStart = true;
			msgs.push(new XmlDeleteMsg().init(XA.append(XA.from(ctn), str.length - endSpLen), endSpLen));
		}
		//trimEnd
		const startSpLen = DOM.txtStartSpLen(str);
		if (startSpLen > 0 && shouldTrimStart(schemaDom, ctn, para)) {
			msgs.push(new XmlDeleteMsg().init(XA.append(XA.from(ctn), 0), startSpLen));
		}
	}

	//Collapse
	const re = DOM.COLLAPSE_WS;
	re.lastIndex = 0;
	let res: RegExpExecArray;
	while ((res = re.exec(str))) {
		if (trimStart && res.index === 0) continue; //déjà traité
		if (trimEnd && res.index === str.length - res[0].length) continue; //déjà traité
		const xa = XA.append(XA.from(ctn), res.index);
		msgs.push(new XmlDeleteMsg().init(xa, res[0].length), new XmlInsertMsg().init(xa, ' '));
	}
}

function shouldTrimStart(schemaDom: SchemaDom, node: Text | Attr, para: Element | Attr): boolean {
	const tw = getTw(para, schemaDom)
	tw.currentNode = node;
	const prev = tw.previousNode();
	return !prev /*début de para =>trim*/ || (prev instanceof Text && DOM.txtEndsWithSp(prev.nodeValue)) /* si text finit par sp =>trim SINON : InlObject ou text qui ne finit pas par sp*/;
}

function shouldTrimEnd(schemaDom: SchemaDom, node: Text | Attr, para: Element | Attr): boolean {
	const tw = getTw(para, schemaDom)
	tw.currentNode = node;
	const next = tw.nextNode();
	return !next /*fin de para =>trim*/ || (next instanceof Text && DOM.txtStartsWithSp(next.nodeValue)) /* si text commence par sp =>trim SINON : InlObject ou text qui ne commence pas par sp*/;
}

function getTw(para: Element | Attr, schemaDom: SchemaDom) {
	return para.ownerDocument.createTreeWalker(para, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, (n) => {
		if (n instanceof Text && n.nodeValue.length > 0) return NodeFilter.FILTER_ACCEPT;
		const skMeta = schemaDom.getSkNode(n)?.rule.skMeta;
		if (skMeta instanceof SkMInlObject) return NodeFilter.FILTER_ACCEPT;
		return skMeta instanceof SkMSpan ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_REJECT /* elt meta...*/;
	});
}

class SkMUL extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	get forceExportContainer() {return true}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement(this.id === "OL" ? "ol" : "ul"));
		super.exportAsHtml(node, rule, p);
	}
}

SKMETALIB.registerMetaNode(new SkMUL('UL'));

// class SkMOL extends SkMTxtRolable {
// }
SKMETALIB.registerMetaNode(new SkMUL('OL'));

// class SkMSL extends SkMTxtRolable {
// }
SKMETALIB.registerMetaNode(new SkMUL('SL'));

class SkMLI extends SkMBase implements ISkMInText {

	initSkRule(skRule: SkRule, confRule: Element, schemaBuilder: ISchemaBuilder) {
		skRule.commentRule = skruleCmtBlackHole;
	}

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	get isParaParent() {return true}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("li"));
		super.exportAsHtml(node, rule, p);
	}
}

SKMETALIB.registerMetaNode(new SkMLI('LI'));


class SkMSLMember extends SkMPara {

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("li"));
		super.exportAsHtml(node, rule, p);
	}
}

SKMETALIB.registerMetaNode(new SkMSLMember('SLMember'));

class SkMTxtCaption extends SkMPara {
	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("caption"));
		super.exportAsHtml(node, rule, p);
	}
}

SKMETALIB.registerMetaNode(new SkMTxtCaption('TxtCaption'));

SKMETALIB.registerMetaNode(new SkMPara('TxtTitle'));

export class SkMSpan extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.inline}

	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr) {return true}

	// isAtBorder(skNode: SkNode, start: boolean, offset: number, schemaDom: SchemaDom): boolean {
	// 	if (start) {
	// 		if (offset > 0) return true;
	// 		//On ne considère pas le noeud meta comme un noeud visible.
	// 		let metaNode: Node = null;
	// 		const ch = skNode.node.firstChild;
	// 		if (ch) {
	// 			const skCh = skNode.schemaDom.getSkNode(ch);
	// 			if (skCh && (skCh.rule.skMeta instanceof SkMMeta)) metaNode = ch;
	// 		}
	// 		for (let n = skNode.node.childNodes.item(offset - 1) as Node; n !== metaNode; n = n.previousSibling) if (!SKMETA.isEmpty(n, schemaDom)) return false;
	// 	} else {
	// 		for (let n = skNode.node.childNodes.item(offset) as Node; n; n = n.nextSibling) if (!SKMETA.isEmpty(n, schemaDom)) return false;
	// 	}
	// 	return true;
	// }

	normChars(schemaDom: SchemaDom, ctn: Text | Attr, msgs: MsgOt[], noTrim?: boolean) {
		const para = ctn instanceof Attr ? ctn : DOM.findParent(ctn, null, (n): n is Element => schemaDom.getSkNode(n)?.rule.skMeta instanceof SkMPara);
		normalizeChars(schemaDom, ctn, para, msgs, noTrim);
	}

	onMergeTextNodes(schemaDom: SchemaDom, ctn: Text, nextTxts: string[]): string {
		return collapseSpOnMergeTextNodes(ctn.nodeValue, nextTxts);
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		if (rule.skFamily) {
			const tag = outParent.appendChild(outParent.ownerDocument.createElement(rule.skFamily));
			super.exportAsHtml(node, rule, tag);
		} else {
			super.exportAsHtml(node, rule, outParent);
		}
	}

	exportContainerNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (parentTarget instanceof DocumentFragment) (parentTarget as IDocumentFragment).xmlSpacePreserve = true;
		return super.exportContainerNode(src, parentTarget, scope, fromDepth, toDepth);
	}
}

SkMSpan.prototype.tryPasteNodes = SkMPara.prototype.tryPasteNodes;

SKMETALIB.registerMetaNode(new SkMSpan('InlPhrase'));
SKMETALIB.registerMetaNode(new SkMSpan('InlLink'));
SKMETALIB.registerMetaNode(new SkMSpan('InlStyle'));
SKMETALIB.registerMetaNode(new SkMSpan('InlTextLeaf'));

/** uLink de type externalUrl */
export class SkMInlExtLink extends SkMSpan {
	initSkRule(skRule: SkRule & ITxtRolableProps, confRule: Element, schemaBuilder: ISchemaBuilder): void {
		skRule.skFamily = "a";
		super.initSkRule(skRule, confRule, schemaBuilder);
	}

	exportAsHtml(node: Element, rule: SkRule, outParent: HTMLElement): void {
		const tag = outParent.appendChild(outParent.ownerDocument.createElement("a"));
		tag.setAttribute("href", node.getAttribute("url"));
		super.exportAsHtmlChildren(node, rule, tag);
	}
}

SKMETALIB.registerMetaNode(new SkMInlExtLink('InlExtLink'));

class SkMInlObject extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.inline}
}

SKMETALIB.registerMetaNode(new SkMInlObject('InlObject'));
SKMETALIB.registerMetaNode(new SkMInlObject('InlImg'));
SKMETALIB.registerMetaNode(new SkMInlObject('InlEmpty'));

class SkMTxtObject extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}
}

SKMETALIB.registerMetaNode(new SkMTxtObject('TxtObject'));
SKMETALIB.registerMetaNode(new SkMTxtObject('TxtEmpty'));

export class SkMTxtTable extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	/**
	 * L'export partiel de table n'exporte que le contenu des cellules pour éviter les incohérences insolubles lignes / colonnes
	 * en fonction du range à exporter.
	 */
	exportNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth == null && toDepth == null) {
			//on prend tout le noeud
			if (parentTarget) JML.appendDomNode(src.node, true, parentTarget);
			//if (scope.deletes) scope.deletes.add(new XmlDeleteMsg().init(XA.from(src.node), 1)); non delete par l'appelant
		} else {
			//Export partiel de la table, on ne va prendre que l'intérieur des cells
			const from = fromDepth != null ? scope.start[fromDepth] || 0 : 0;
			if (typeof from === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.start}`);
			const to = toDepth != null && toDepth < scope.end.length ? scope.end[toDepth] : Infinity;
			if (typeof to === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.end}`);

			let ch = from === 0 ? src.node.firstChild : src.node.childNodes.item(from);
			let i = from;
			//copie partielle de from.
			if (ch && i < to && fromDepth != null && fromDepth < scope.start.length - 1) {
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode) {
					const skMeta = subSkNode.rule.skMeta;
					if (skMeta instanceof SkMTxtRow) {
						subSkNode.rule.skMeta.exportNode(subSkNode, parentTarget, scope, fromDepth + 1, null);
					} else if (skMeta instanceof SkMTxtCaption) {
						//todo transformer en simple para dans parentTarget ?
						subSkNode.rule.skMeta.exportNode(subSkNode, parentTarget, scope, fromDepth + 1, null);
					}
				}
				ch = ch.nextSibling;
				i++;
			}
			//copie des noeuds après from et avant to.
			while (ch && i < to) {
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode) {
					if (subSkNode.rule.skMeta instanceof SkMTxtRow) {
						if (parentTarget) subSkNode.rule.skMeta.exportContent(subSkNode, parentTarget);
						//On ne suppr pas les rows et cells (structures stables).
					} else if (subSkNode.rule.skMeta instanceof SkMTxtCaption) {
						//todo transformer en simple para dans parentTarget ?
						if (parentTarget) subSkNode.rule.skMeta.exportContent(subSkNode, parentTarget);
						if (scope.deletes) scope.deletes.add(new XmlDeleteMsg().init(XA.from(ch), 1));
					}
				}
				ch = ch.nextSibling;
				i++;
			}
			//copie partielle de to.
			if (ch && toDepth != null && toDepth < scope.end.length - 1) {
				//on est dans la hiérarchie de scope.end
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode && subSkNode.rule.skMeta instanceof SkMTxtRow) {
					subSkNode.rule.skMeta.exportNode(subSkNode, parentTarget, scope, null, toDepth + 1);
				}
			}
		}
		return parentTarget;
	}


	exportNodeCustom(src: SkNode, filters: Jsonisable): Node {
		if (filters && filters.tableRect) {
			const tableRect = filters.tableRect as { startRow: number, startCol: number, endRow: number, endCol: number };
			const result = src.node.cloneNode(false);
			let ch = src.node.firstElementChild;
			let skCh = ch ? src.schemaDom.getSkNode(ch) : null;
			if (skCh.rule.skMeta instanceof SkMMeta) {
				//si 1er fils meta, on copie. XXX rendre paramétrable dans les filters ?
				result.appendChild(skCh.rule.skMeta.exportNodeCustom(skCh, null));
				ch = ch.nextElementSibling;
				skCh = ch ? src.schemaDom.getSkNode(ch) : null;
			}
			let idxCol = 0;
			while (skCh && !(skCh.rule.skMeta instanceof SkMTxtRow)) {
				if (skCh.rule.skMeta instanceof SkMTxtCol) {
					if (idxCol >= tableRect.startCol && idxCol < tableRect.endCol) {
						//On copie la col.
						result.appendChild(skCh.rule.skMeta.exportNodeCustom(skCh, null));
					}
					idxCol++;
				}
				ch = ch.nextElementSibling;
				skCh = ch ? src.schemaDom.getSkNode(ch) : null;
			}
			let idxRow = 0;
			let prevRow: LogicRow;
			while (ch) {
				const skChM = skCh.rule.skMeta;
				if (skChM instanceof SkMTxtRow) {
					const logicRow = skChM.buildLogicRow(skCh, prevRow);
					if (idxRow >= tableRect.startRow && idxRow < tableRect.endRow) {
						//On est dans les lignes exportées
						const tr = skChM.exportContainerNode(skCh, result) as Element;
						for (let c = tableRect.startCol; c < tableRect.endCol; c++) {
							const logicCell = logicRow[c];
							if (logicCell instanceof SkNode) {
								tr.appendChild(logicCell.rule.skMeta.exportNodeCustom(logicCell, null));
							} else if (logicCell.masterCell === logicCell) {
								//On est le master d'une cell, on la recopie en tronquant éventuellement les rowSpan/colSpan
								const skCellM = logicCell.ownerCell.rule.skMeta as SkMTxtCell;
								const cell = tr.appendChild(skCellM.exportNodeCustom(logicCell.ownerCell, null)) as Element;
								if (logicCell.colSpan > tableRect.endCol - c) {
									skCellM.setColSpan(logicCell.ownerCell.rule, cell, tableRect.endCol - c);
								}
								if (logicCell.rowSpan > tableRect.endRow - idxRow) {
									skCellM.setRowSpan(logicCell.ownerCell.rule, cell, tableRect.endRow - idxRow);
								}
							} else if (c === tableRect.startCol && logicCell.rowSpan === logicCell.masterCell.rowSpan) {
								//on est la 1ère cell de la row
								//cette cell est absorbée par un master de notre ligne => hors tableRect demandé
								//=> on cree une cell vide pour compenser.
								const skCellM = logicCell.ownerCell.rule.skMeta as SkMTxtCell;
								const cell = skCellM.exportContainerNode(logicCell.ownerCell, tr) as Element;
								skCellM.setColSpan(logicCell.ownerCell.rule, cell, Math.min(logicCell.colSpan, tableRect.endCol - c));
								skCellM.setRowSpan(logicCell.ownerCell.rule, cell, Math.min(logicCell.rowSpan, tableRect.endRow - idxRow));
							}
						}
					}
					prevRow = logicRow;
					idxRow++;
				}
				ch = ch.nextElementSibling;
				skCh = ch ? src.schemaDom.getSkNode(ch) : null;
			}
			return result;
		}
		return super.exportNodeCustom(src, filters);
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("table"));
		super.exportAsHtml(node, rule, p);
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.nodeDatas?.has(nodeOrAttr)) return; //déjà traité
		if (!this.checkTableInTable(skCtx, rule)) return;
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
		const skTable = skCtx.skNode;
		//console.log("validTable::::", skTable.node);
		let countCols = 0;
		const rows: LogicRow[] = [];
		let ch = skTable.node.firstElementChild;
		let prevRow: LogicRow;
		let maxCellsInRow = 1; //1 mini
		let lastCol: SkNode;
		while (ch) {
			const skCh = skTable.schemaDom.getSkNode(ch);
			if (skCh == null) return;//on interrompt la validation : ce noeud doit être validé avant...
			const skChM = skCh.rule.skMeta;
			if (skChM instanceof SkMTxtRow) {
				rows.push((prevRow = skChM.buildLogicRow(skCh, prevRow)));
				if (prevRow == null) return; //on interrompt la validation : un noeud fils doit être validé avant...
				if (prevRow.length > maxCellsInRow) maxCellsInRow = prevRow.length;
			} else if (skChM instanceof SkMTxtCol) {
				countCols++;
				lastCol = skCh;
			}
			ch = ch.nextElementSibling;
		}
		//Validation de la table possible, on ajoute un flag indiquant que la table est validée.
		(skCtx.execOptions.nodeDatas || (skCtx.execOptions.nodeDatas = new Map())).set(skTable.node, true);

		const maxCols = Math.max(countCols, maxCellsInRow);
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			if (row.length < maxCols) {
				//Problème : Cette row manque de cellules
				if (skCtx.execOptions.autoComplete) {
					const skRuleCell = row.skRow.rule.contentRule.findRule((r: SkRule) => r.skMeta instanceof SkMTxtCell);
					const newCells = [] as IJmlSet;
					for (let i = row.length; i < maxCols; i++) skRuleCell.createContent(newCells);
					skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(row.skRow.node), row.skRow.node.childNodes.length), newCells));
				} else if (skCtx.execOptions.genAnnots) {
					skCtx.addAnnot(new SkAnnotMissing().init('tableCellMissing', row.skRow.node, row.skRow.node.childNodes.length, row.skRow.rule.contentRule,
						"Des cellules manquent dans cette ligne du tableau."));
				}
			}
			if (row.hasHoles()) {
				//Des cells supérieures avec rowSpan créent des trous.
				if (skCtx.execOptions.autoComplete) {
					const skRuleCell = row.skRow.rule.contentRule.findRule((r: SkRule) => r.skMeta instanceof SkMTxtCell);
					let prevCell: SkNode;
					for (let i = 0; i < row.length; i++) {
						const cell = row[i];
						if (cell == null) {
							const newCells = [] as IJmlSet;
							skRuleCell.createContent(newCells);
							while (++i < row.length && row[i] == null) skRuleCell.createContent(newCells);
							i--;
							skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(row.skRow.node), prevCell ? DOM.computeOffset(prevCell.node) + 1 : 0), newCells));
						} else if (cell instanceof SkNode) {
							prevCell = cell;
						}
					}
				} else if (skCtx.execOptions.genAnnots) {
					skCtx.addAnnot(new SkAnnotMissing().init('tableCellMissing', row.skRow.node, row.skRow.node.childNodes.length, row.skRow.rule.contentRule,
						"Des cellules manquent dans cette ligne du tableau."));
				}
			}
		}
		const lastRow = rows[rows.length - 1];
		if (lastRow) {
			for (let i = 0; i < lastRow.length; i++) {
				const cell = lastRow[i];
				if (cell instanceof MergedCell && cell.rowSpan > 1) {
					//Problème : rowSpan dépasse la fin du tableau.
					if (skCtx.execOptions.autoComplete) {
						const attName = (cell.ownerCell.rule as ISkMTxtCellProps).rowSpanAttName;
						const currentSpan = parseInt((cell.ownerCell.node as Element).getAttribute(attName), 10);
						skCtx.execOptions.corrections.push(new XmlStrMsg().init(XA.append(XA.from(cell.ownerCell.node), attName), (currentSpan - cell.rowSpan + 1).toString()));
					} else if (skCtx.execOptions.genAnnots) {
						skCtx.addAnnot(new SkAnnotWrongValue().init(lastRow.skRow.node as Element,
							"Une fusion de cellules déborde par rapport au nombre de lignes."));
					}
					let nextR = lastRow[i + 1];
					while (nextR instanceof MergedCell && nextR.ownerCell === cell.ownerCell) {
						//On passe les mergedCells suivantes déjà traitées.
						nextR = lastRow[(++i) + 1];
					}
				}
			}
		}
		if (maxCellsInRow > countCols) {
			//Problème : Pas assez de colonnes déclarées
			//NOTE: en dernier pour que l'insertion des cells ait été validées (présence de skNode sur les elts de cells) avant la validation de la table.
			const skRuleCol = skTable.rule.contentRule.findRule((r: SkRule) => r.skMeta instanceof SkMTxtCol);
			if (skCtx.execOptions.autoComplete) {
				let offset;
				if (lastCol) {
					offset = DOM.computeOffset(lastCol.node) + 1;
				} else {
					const firstRow = DOM.findFirstChild(skTable.node, (n: Node): n is Element => {
						const skCh = n instanceof Element ? skTable.schemaDom.getSkNode(n) : null;
						return skCh && (skCh.rule.skMeta instanceof SkMTxtRow);
					});
					offset = firstRow ? DOM.computeOffset(firstRow) : skTable.node.childNodes.length;
				}
				const newCol = [] as IJmlSet;
				for (let i = countCols; i < maxCellsInRow; i++) skRuleCol.createContent(newCol);
				skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(skTable.node), offset), newCol));
			} else if (skCtx.execOptions.genAnnots) {
				skCtx.addAnnot(new SkAnnotMissing().init('tableColMissing', skTable.node, 0, skRuleCol,
					"Des lignes contiennent plus de cellules que de colonnes déclarées."));
			}
		}

		if (rows.length === 0) {
			//pas de rows, on en crée une
			if (skCtx.execOptions.autoComplete) {
				const skRuleRow = skTable.rule.contentRule.findRule((r: SkRule) => r.skMeta instanceof SkMTxtRow) as SkRuleNode;
				const newRow = [] as IJmlSet;
				skRuleRow.createContent(newRow);
				const skRuleCell = skRuleRow.contentRule.findRule((r: SkRule) => r.skMeta instanceof SkMTxtCell);
				let newCells: IJmlSet = JML.getJmlChildrenOrBody(newRow, 0) as IJmlSet;
				if (!newCells) newRow.push((newCells = []));
				for (let i = 0; i < maxCols; i++) skRuleCell.createContent(newCells);
				skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(skTable.node), skTable.node.childNodes.length), newRow));
			} else if (skCtx.execOptions.genAnnots) {
				skCtx.addAnnot(new SkAnnotMissing().init('tableRowMissing', skTable.node, skTable.node.childNodes.length, skTable.rule.contentRule,
					"Au moins une ligne est requise dans le tableau."));
			}
		}
	}

	/** Interdit une table dans une table (contrainte XulRunner): TODO A rendre configurable dans SC5.1 ? */
	protected checkTableInTable(skCtx: SkContext, rule: ISkRuleObj): boolean {
		const from = skCtx.skNode.node as Element;
		const schemaDom = skCtx.skNode.schemaDom;
		let par = from.parentNode;
		while (par && par.namespaceURI === DOM.SCCORE_NS) {
			const skPar = schemaDom.getSkNode(par);
			if (skPar && skPar.rule.skMeta instanceof SkMTxtCell) {
				if (skCtx.execOptions.autoCleanup) {
					skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(from), 1));
				} else if (skCtx.execOptions.genAnnots) {
					skCtx.addAnnot(new SkAnnotEltUnknown().init(from, skCtx.skNode.rule));
				}
				return false;
			}
			par = par.parentNode;
		}
		return true;
	}


	async tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		if (!isSkTableImportContext(ctx)) return super.tryPasteNodes(ctx, content, cache);
		return (await import("/@lib@/edit/import/importTable.js")).tryImportInTable(this, ctx, content, cache);
	}
}

SKMETALIB.registerMetaNode(new SkMTxtTable('TxtTable'));


/** Import de cells de tableaux. */
export interface OSkTableImportContext extends OSkPasteContext {
	/** Selection courante des cellules du tableau, point de départ pour l'import. */
	cellInsertPoint: IXAddr
}

function isSkTableImportContext(importCtx: OSkPasteContext): importCtx is OSkTableImportContext {return (importCtx as OSkTableImportContext).cellInsertPoint != null}


export class SkMTxtCol extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	setWidthInPx(skRule: SkRule & ISkMTxtColProps, cell: Element, width: number) {
		//TODO customisable... skRule.widthColAttName
		//TODO facteur px -> char '/ 6' ?
		cell.setAttribute(skRule.widthAttName || "width", Math.max(2, Math.round(width / 6)).toString());
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.resetAll) return; //la validation se fera plus haut.
		if (!skCtx.execOptions.genAnnots && !skCtx.execOptions.autoComplete) return; //pas de validation
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
		const table = skCtx.node.parentElement;
		if (!table) return;
		const skTable = skCtx.skNode.schemaDom.getSkNode(table);
		if (skTable && skTable.rule.skMeta instanceof SkMTxtTable) {
			(skCtx.execOptions.shouldRevalid || (skCtx.execOptions.shouldRevalid = new Set())).add(skTable.node as Element);
		}
	}

}

SKMETALIB.registerMetaNode(new SkMTxtCol('TxtCol'));

interface ISkMTxtColProps {
	widthAttName?: string
}


export class SkMTxtRow extends SkMTxtRolable {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	get forceExportContainer() {return true}

	exportNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth == null && toDepth == null) {
			//on prend tout le noeud
			if (parentTarget) JML.appendDomNode(src.node, true, parentTarget);
			//if (scope.deletes) scope.deletes.add(new XmlDeleteMsg().init(XA.from(src.node), 1)); non delete par l'appelant
		} else {
			//Export partiel du row, on ne va prendre que l'intérieur des cells
			const from = fromDepth != null ? scope.start[fromDepth] || 0 : 0;
			if (typeof from === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.start}`);
			const to = toDepth != null && toDepth < scope.end.length ? scope.end[toDepth] : Infinity;
			if (typeof to === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.end}`);

			let ch = from === 0 ? src.node.firstChild : src.node.childNodes.item(from);
			let i = from;
			//copie partielle de from.
			if (ch && i < to && fromDepth != null && fromDepth < scope.start.length - 1) {
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode && subSkNode.rule.skMeta instanceof SkMTxtCell) {
					subSkNode.rule.skMeta.exportNode(subSkNode, parentTarget, scope, fromDepth + 1, null);
				}
				ch = ch.nextSibling;
				i++;
			}
			//copie des noeuds après from et avant to.
			while (ch && i < to) {
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode && subSkNode.rule.skMeta instanceof SkMTxtCell) {
					subSkNode.rule.skMeta.exportContent(subSkNode, parentTarget);
				}
				ch = ch.nextSibling;
				i++;
			}
			//copie partielle de to.
			if (ch && toDepth != null && toDepth < scope.end.length - 1) {
				//on est dans la hiérarchie de scope.end
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode && subSkNode.rule.skMeta instanceof SkMTxtCell) {
					subSkNode.rule.skMeta.exportNode(subSkNode, parentTarget, scope, null, toDepth + 1);
				}
			}
		}
		return parentTarget;
	}

	/** On n'exporte que le contenu des cells. */
	exportContent(src: SkNode, parentTarget: Node | IJmlSet) {
		let ch = src.node.firstChild;
		while (ch) {
			const skCh = src.schemaDom.getSkNode(ch);
			if (skCh && (skCh.rule.skMeta instanceof SkMTxtCell)) {
				skCh.rule.skMeta.exportContent(skCh, parentTarget);
			}
			ch = ch.nextSibling;
		}
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("tr"));
		super.exportAsHtml(node, rule, p);
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.resetAll) return; //la validation se fera plus haut.
		if (!skCtx.execOptions.genAnnots && !skCtx.execOptions.autoComplete) return; //pas de validation
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
		const table = skCtx.node.parentElement;
		if (!table) return;
		const skTable = skCtx.skNode.schemaDom.getSkNode(table);
		if (skTable && skTable.rule.skMeta instanceof SkMTxtTable) {
			(skCtx.execOptions.shouldRevalid || (skCtx.execOptions.shouldRevalid = new Set())).add(skTable.node as Element);
		}
	}

	buildLogicRow(skRow: SkNode, previousRow?: LogicRow): LogicRow {
		const logicRow = new LogicRow(skRow, previousRow);
		let cell = skRow.node.firstElementChild;
		while (cell) {
			const skCell = skRow.schemaDom.getSkNode(cell);
			if (skCell == null) return null; //on interrompt la vaidation : cette cell doit être validée avant...
			if (skCell.rule.skMeta instanceof SkMTxtCell) {
				const spans = skCell.rule.skMeta.getSpans(skCell);
				if (spans != null) {
					const master = new MergedCell(skCell, spans.rowSpan, spans.colSpan);
					logicRow.addCell(master);
					for (let i = 1; i < spans.colSpan; i++) logicRow.addCell(new MergedCell(skCell, spans.rowSpan, spans.colSpan - i, master));
				} else {
					logicRow.addCell(skCell);
				}
			}
			cell = cell.nextElementSibling;
		}
		return logicRow;
	}

}

SKMETALIB.registerMetaNode(new SkMTxtRow('TxtRow'));

export class SkMTxtCell extends SkMTxtRolable implements ISkMInText {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	initSkRule(skRule: SkRule & ISkMTxtCellProps, confRule: Element, schemaBuilder: ISchemaBuilder): void {
		super.initSkRule(skRule, confRule, schemaBuilder);
		skRule.rowSpanAttName = confRule.getAttributeNS(SK_NS, 'rowSpanAtt');
		skRule.colSpanAttName = confRule.getAttributeNS(SK_NS, 'colSpanAtt');
	}

	get isParaParent() {return true}

	exportContainerNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth != null || toDepth != null) return null; //si copie partielle de la cell, on n'exporte pas la cell.
		return super.exportContainerNode(src, parentTarget, scope, fromDepth, toDepth);
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("td"));
		const spans = this.getSpans({rule, node} as SkNode);
		if (spans) {
			if (spans.rowSpan > 1) p.setAttribute("rowspan", spans.rowSpan.toString());
			if (spans.colSpan > 1) p.setAttribute("colspan", spans.colSpan.toString());
		}
		super.exportAsHtml(node, rule, p);
	}

	getSpans(skCell: SkNode): null | { rowSpan: number, colSpan: number } {
		const rule = skCell.rule as ISkMTxtCellProps;
		const rowSpan = rule.rowSpanAttName ? (skCell.node as Element).getAttribute(rule.rowSpanAttName) : null;
		const colSpan = rule.colSpanAttName ? (skCell.node as Element).getAttribute(rule.colSpanAttName) : null;
		const rowSpanInt = rowSpan ? parseInt(rowSpan, 10) || 1 : 1;
		const colSpanInt = colSpan ? parseInt(colSpan, 10) || 1 : 1;
		return rowSpanInt > 1 || colSpanInt > 1 ? {rowSpan: Math.max(1, rowSpanInt), colSpan: Math.max(1, colSpanInt)} : null;
	}

	setRowSpan(skRule: SkRule & ISkMTxtCellProps, cell: Element, span: number) {
		if (span > 1) cell.setAttribute(skRule.rowSpanAttName, span.toString(10));
		else cell.removeAttribute(skRule.rowSpanAttName);
	}

	setColSpan(skRule: SkRule & ISkMTxtCellProps, cell: Element, span: number) {
		if (span > 1) cell.setAttribute(skRule.colSpanAttName, span.toString(10));
		else cell.removeAttribute(skRule.colSpanAttName);
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.autoCleanup && skCtx.execOptions.corrections.length > 0) skipUnknownTxtNode(skCtx, rule as SkRuleNode, nodeOrAttr as Element);
		if (skCtx.execOptions.resetAll) return; //la validation se fera plus haut.
		if (!skCtx.execOptions.genAnnots && !skCtx.execOptions.autoComplete) return; //pas de validation
		textGlobalCheck(this, skCtx, rule, nodeOrAttr as Node);
		let table = skCtx.node.parentElement;
		table = table ? table.parentElement : null;
		if (!table) return;
		const skTable = skCtx.skNode.schemaDom.getSkNode(table);
		if (skTable && skTable.rule.skMeta instanceof SkMTxtTable) {
			(skCtx.execOptions.shouldRevalid || (skCtx.execOptions.shouldRevalid = new Set())).add(skTable.node as Element);
		}
	}
}

SKMETALIB.registerMetaNode(new SkMTxtCell('TxtCell'));

export class SkMTxtDiv extends SkMTxtRolable implements ISkMInText {

	get gramLevelType(): EGramLevelType {return EGramLevelType.text}

	get isParaParent() {return true}

	exportAsHtml(node: Node, rule: SkRule & ITxtRolableProps, outParent: HTMLElement): void {
		const p = outParent.appendChild(outParent.ownerDocument.createElement("div"));
		if (rule.role) p.setAttribute("class", rule.role);
		super.exportAsHtml(node, rule, p);
	}
}

SKMETALIB.registerMetaNode(new SkMTxtDiv('TxtDiv'));

interface ISkMTxtCellProps {
	rowSpanAttName?: string
	colSpanAttName?: string
}

export class LogicRow extends Array<SkNode | MergedCell | null> {

	skRow: SkNode;

	protected withHoles: boolean;

	constructor(skRow: SkNode, prev: LogicRow) {
		super();
		this.skRow = skRow;
		if (prev) for (let i = 0; i < prev.length; i++) {
			const c = prev[i];
			if (c instanceof MergedCell && c.rowSpan > 1) {
				this[i] = new MergedCell(c.ownerCell, c.rowSpan - 1, c.colSpan, c);
				this.withHoles = true;
			}
		}
	}

	hasHoles(): boolean {
		if (!this.withHoles) return false;
		for (let i = 0; i < this.length; i++) if (this[i] == null) return true;
		return false;
	}

	addCell(cell: SkNode | MergedCell) {
		if (this.withHoles) for (let i = 0; i < this.length; i++) {
			if (this[i] == null) {
				this[i] = cell;
				return;
			}
		}
		this.push(cell);
	}
}

export class MergedCell {
	/** Cellule réelle absorbant cet espace. */
	ownerCell: SkNode;
	/** Cellule réelle absorbant cet espace. */
	masterCell: MergedCell;
	/** Nb de rows (y compris celle-ci) qui sont fusionnées après cette cell. */
	rowSpan: number;
	/** Nb de cols (y compris celle-ci) qui sont fusionnées après cette cell. */
	colSpan: number;

	constructor(ownerCell: SkNode, rowSpan: number, colSpan: number, master?: MergedCell) {
		this.ownerCell = ownerCell;
		this.masterCell = master || this;
		this.rowSpan = rowSpan;
		this.colSpan = colSpan;
	}

	get isMaster() {return this === this.masterCell}
}

/**
 * Cleanup des noeuds inconnus dans les fils d'un noeud Txt.
 * Tente de compenser la suppr pur et simple par le schema d'un noeud fils inconnu
 * en ré-insérant ses éventuels sous-noeuds qui seraient connus.
 * L'enjeu est de présever l'entièreté des phrases et paragraphes même si des structures
 * intermédiaires enveloppantes sont inconnues dans ce schéma.
 */
function skipUnknownTxtNode(skCtx: SkContext, rule: SkRuleNode, elt: Element) {
	const schema = skCtx.skNode.schemaDom;
	for (let ch = elt.firstChild; ch; ch = ch.nextSibling) {
		const skNode = schema.getSkNode(ch);
		if (skNode && skNode.rule instanceof SkRuleEltUnknown && ch.namespaceURI === DOM.SCCORE_NS && ch.hasChildNodes()) {
			//elt inconnu mais de ns SC, donc probablement du texte, le schema a déjà supprimé cette balise
			//on parcourt son contenu pour tenter de préserver des fragments autorisés qu'on va réinjecter à la place.
			const tw = elt.ownerDocument.createTreeWalker(ch);
			tw.nextNode();
			let jml: IJmlSet;
			do {
				if (rule.contentRule.findRuleNodeFor(tw.currentNode)) {
					//Aurorisé, on ajoute
					if (!jml) jml = JML.dom2jml(tw.currentNode);
					else JML.dom2jml(tw.currentNode, jml);
					if (!tw.nextSibling() && !tw.nextNode()) break;
				} else if (!tw.nextNode()) break;
			} while (true);
			if (jml) skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.from(ch), jml));
		}
	}
}

const skruleCmtBlackHole = new SkRuleComment().initSkMeta(SKMETALIB.getMetaNode('BlackHole'));
